import React, { useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  ButtonBase,
  Chip,
  CircularProgress,
  DialogContent,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import UploadIcon from '@mui/icons-material/Upload';
import LooksTwoIcon from '@mui/icons-material/LooksTwo';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import LooksOneIcon from '@mui/icons-material/LooksOne';
import { getBranches, Branch } from 'api/branchApi';
import { listClients, Client } from 'api/clientsApi';
import { getLabourBillingRatesTemplateFilename, ImportMode } from 'api/labourApi';
import {
  countryList,
  getStateOrProvinceListByCountry,
  getStateOrProvinceOrRegionLabelByCountry,
} from 'util/countryAndStateUtil';
import { isComboReady } from '../Utils/Utils';

interface Props {
  mode: ImportMode;
  file: File | null;
  setFile: (file: File | null) => void;
  isLoading: boolean;
  descriptionText: string;
  infoText: string;
  branch: Branch | null;
  setBranch: (branch: Branch | null) => void;
  client: Client | null;
  setClient: (client: Client | null) => void;
  onDownloadTemplate: () => void;
  isDownloadingTemplate: boolean;
  scopeError?: { title: string; message: string } | null;
  noExistingDataMessage?: string | null;
}

// Check if the banch - client combination is  ready
export const isBillingComboReady = (branch: Branch | null, client: Client | null): boolean =>
  isComboReady('sell', branch, client);

const getFileName = (name: string) => {
  const dotIndex = name.lastIndexOf('.');
  const base = dotIndex > 0 ? name.slice(0, dotIndex) : name;
  const ext = dotIndex > 0 ? name.slice(dotIndex) : '';
  return base.length > 30 ? `${base.substring(0, 30)}...${ext}` : name;
};

const SELECT_FIELD_STYLE = {
  fontSize: '0.9rem',
  backgroundColor: 'white',
  marginBottom: 20,
};

const LabourScopeBillingRatesForm: React.FC<Props> = ({
  mode,
  file,
  setFile,
  isLoading,
  descriptionText,
  infoText,
  branch,
  setBranch,
  client,
  setClient,
  onDownloadTemplate,
  isDownloadingTemplate,
  scopeError,
  noExistingDataMessage,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { data: branches, isFetching: isFetchingBranches } = useQuery(['branches'], getBranches);
  const { data: clients, isFetching: isFetchingClients } = useQuery(['list-clients'], listClients);

  const [country, setCountry] = useState(countryList.Australia);
  const [state, setState] = useState('');

  const noBranchesAvailable = !isFetchingBranches && !!branches && branches.length === 0;
  const noClientsAvailable = !isFetchingClients && !!clients && clients.length === 0;

  const filteredBranches = (branches || []).filter((b) => b.country === country && b.state === state);
  const noBranchesForLocation =
    !noBranchesAvailable && !!country && !!state && filteredBranches.length === 0;

  const nothingSelectable = noBranchesAvailable && noClientsAvailable;
  const comboReady = isBillingComboReady(branch, client);
  const tilesDisabled = isLoading || isDownloadingTemplate;

  return (
    <DialogContent>
      <Typography variant="body2" sx={{ marginBottom: '1rem' }}>
        {descriptionText}
      </Typography>

      <Alert severity="info" sx={{ marginBottom: '1rem', textAlign: 'justify' }}>
        {infoText}
      </Alert>

      {nothingSelectable && (
        <Alert severity="error" sx={{ marginBottom: '1rem' }}>
          No branches or clients available. Please create one first.
        </Alert>
      )}

      {!noBranchesAvailable && (
        <>
          <Stack direction="row" spacing={2}>
            <TextField
              fullWidth
              select
              label="Select Country"
              variant="outlined"
              style={SELECT_FIELD_STYLE}
              value={country}
              onChange={(e) => {
                setCountry(e.target.value as countryList);
                setState('');
                setBranch(null);
              }}>
              {Object.values(countryList).map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </TextField>

            {country && (
              <TextField
                fullWidth
                select
                label={`Select ${getStateOrProvinceOrRegionLabelByCountry(country)}`}
                variant="outlined"
                style={SELECT_FIELD_STYLE}
                value={state}
                onChange={(e) => {
                  setState(e.target.value);
                  setBranch(null);
                }}>
                {getStateOrProvinceListByCountry(country)?.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </TextField>
            )}

            {country && state && filteredBranches.length > 0 && (
              <TextField
                fullWidth
                select
                label="Select Branch"
                variant="outlined"
                style={SELECT_FIELD_STYLE}
                value={branch?.id || ''}
                onChange={(e) => {
                  setBranch(filteredBranches.find((b) => b.id === e.target.value) || null);
                }}>
                {filteredBranches.map((b) => (
                  <MenuItem key={b.id} value={b.id}>
                    {b.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Stack>

          {noBranchesForLocation && (
            <Alert severity="error" sx={{ marginBottom: '1rem' }}>
              No branches available for {state}, {country}. Please select a different location or create a
              branch for this location first.
            </Alert>
          )}
        </>
      )}

      {!noClientsAvailable && (
        <TextField
          select
          fullWidth
          label="Select Client"
          variant="outlined"
          style={SELECT_FIELD_STYLE}
          value={client?.id || ''}
          onChange={(e) => setClient(clients?.find((c) => c.id === e.target.value) || null)}>
          {(clients || []).map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {c.legalName}
            </MenuItem>
          ))}
        </TextField>
      )}

      {scopeError && (
        <Alert severity="error" sx={{ marginBottom: '1rem' }}>
          <AlertTitle>{scopeError.title}</AlertTitle>
          {scopeError.message}
        </Alert>
      )}

      {!nothingSelectable && !comboReady && (
        <Alert severity="info" sx={{ marginBottom: '1rem' }}>
          Select a branch, a client, or both above to reveal the template download and upload options.
        </Alert>
      )}

      {comboReady && noExistingDataMessage && (
        <Alert severity="error" sx={{ marginBottom: '1rem' }}>
          {noExistingDataMessage}
        </Alert>
      )}

      {comboReady && !noExistingDataMessage && (
        <Box sx={{ display: 'flex', gap: 3, borderRadius: '8px' }}>
          <ButtonBase sx={{ flex: 1 }} disabled={tilesDisabled} onClick={onDownloadTemplate}>
            <Box
              sx={{
                width: '100%',
                borderRadius: '8px',
                border: '1px solid',
                borderColor: 'divider',
                height: '12.6rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <LooksOneIcon
                sx={{ position: 'absolute', top: 10, left: 10, color: '#737F9C' }}
                fontSize="medium"
              />
              <Stack alignItems="center">
                {isDownloadingTemplate ? (
                  <CircularProgress size={32} sx={{ color: '#737F9C' }} />
                ) : (
                  <DownloadIcon sx={{ color: '#737F9C' }} fontSize="large" />
                )}
                <Typography variant="body2" gutterBottom sx={{ fontWeight: 700 }}>
                  Download template
                </Typography>
                <Chip
                  label={getLabourBillingRatesTemplateFilename(mode, branch?.name, client?.legalName)}
                  sx={{ marginTop: 1, bgcolor: '#F4F5F9', color: '#737F9C' }}
                  icon={<DownloadIcon />}
                />
              </Stack>
            </Box>
          </ButtonBase>

          <ButtonBase component="label" disabled={tilesDisabled} sx={{ flex: 1, borderRadius: '8px' }}>
            <input
              type="file"
              accept=".xlsx"
              disabled={tilesDisabled}
              hidden
              onChange={(e) => {
                const selected = e.target.files?.[0];
                e.target.value = '';
                if (!selected) return;
                if (!selected.name.toLowerCase().endsWith('.xlsx')) {
                  enqueueSnackbar('Please select an .xlsx file', { variant: 'error' });
                  return;
                }
                setFile(selected);
              }}
            />
            <Box
              sx={{
                width: '100%',
                borderRadius: '8px',
                border: '1px solid',
                borderColor: 'divider',
                height: '12.6rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <LooksTwoIcon
                sx={{ position: 'absolute', top: 10, left: 10, color: '#737F9C' }}
                fontSize="medium"
              />
              <Stack alignItems="center">
                <CloudUploadIcon sx={{ color: '#737F9C' }} fontSize="large" />
                <Typography variant="body2" gutterBottom sx={{ fontWeight: 700 }}>
                  Upload .xlsx
                </Typography>
                <Typography variant="caption">Uploaded file must follow the template.</Typography>
                {file && (
                  <Chip
                    label={getFileName(file.name)}
                    sx={{ marginTop: 1, bgcolor: '#F4F5F9', color: '#737F9C' }}
                    icon={<UploadIcon />}
                  />
                )}
              </Stack>
            </Box>
          </ButtonBase>
        </Box>
      )}
      {isLoading && (
        <Box mt={2} display="flex" justifyContent="center" flexDirection="column">
          <Typography variant="caption" color="textSecondary">
            This process may take a while depending on the size of the file.
          </Typography>
          <LinearProgress sx={{ width: '100%', marginTop: 5 }} />
        </Box>
      )}
    </DialogContent>
  );
};

export default LabourScopeBillingRatesForm;
